// src/pages/MyDocuments.js
//
// User-facing list of club documents the signed-in user is allowed to see,
// filtered by audience tag match. Replaces the need for users to know slug
// URLs — they can browse anything tagged for one of their audiences.
//
// Route: /my-documents (auth-gated like other /my-* routes)
//
// Audience tag resolution mirrors DocViewer:
//   • everyone  — included for everyone (admins through anonymous)
//   • admins    — users with 'admin' role
//   • coaches   — users with 'coach' or 'assistant_coach' role
//   • adults    — users with 'adult_athlete' / 'player' / 'member' role
//   • parents   — users with users.isParent == true (set by syncParentFlag CF)
//
// The query requires where('enabled', '==', true) to satisfy the rule
// (which checks resource.data.enabled). We pull all enabled docs and filter
// audience client-side, since Firestore can't do array-overlap queries.

import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, doc as fsDoc, getDoc } from 'firebase/firestore';
import { isActiveMember } from '../utils/membership';
import { onAuthStateChanged } from 'firebase/auth';
import { db, auth } from '../firebase';
import {
  Box, Paper, Typography, CircularProgress, Alert, Chip,
} from '@mui/material';
import ArticleIcon from '@mui/icons-material/Article';
import Layout from '../components/Layout';
import { useBranding } from '../TournamentBrandingContext';
import { logError } from '../utils/errorLogger';

async function resolveViewerAudiences(user) {
  const tags = new Set(['everyone']);
  if (!user) return tags;
  try {
    const uDoc = await getDoc(fsDoc(db, 'users', user.uid));
    if (!uDoc.exists()) return tags;
    const data = uDoc.data();
    const roles = data.roles || [];
    if (roles.includes('admin')) tags.add('admins');
    if (roles.includes('coach') || roles.includes('assistant_coach')) tags.add('coaches');
    // Membership comes from the map, not the roles array — day passes no longer
    // write a 'member' role, and a lapsed one would leave it behind anyway.
    if (roles.includes('adult_athlete') || roles.includes('player')
        || roles.includes('member') || isActiveMember(data)) {
      tags.add('adults');
    }
    if (data.isParent === true) tags.add('parents');
  } catch (err) {
    logError('MyDocuments', 'audience resolve failed: ' + err.message);
  }
  return tags;
}

const AUDIENCE_LABEL = {
  everyone: 'Public',
  parents: 'Parents',
  adults: 'Adults',
  coaches: 'Coaches',
  admins: 'Admins',
};

export default function MyDocuments() {
  const navigate = useNavigate();
  const branding = useBranding();
  const [loading, setLoading] = useState(true);
  const [docs, setDocs] = useState([]);
  const [error, setError] = useState('');

  // Branding falls back to the AdminBranding defaults if context is empty
  // for any reason (page bg #f0f4f8, primary #1E3A5F, accent #C9A84C).
  const primary = branding?.primaryColor || '#1E3A5F';
  const accent = branding?.accentColor || '#C9A84C';

  useEffect(() => {
    let cancelled = false;
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (cancelled) return;
      setLoading(true); setError('');
      // ── DIAG ────────────────────────────────────────────────────────────
      // Each step is logged so we can see exactly where a failure happens
      // and what values are in play (auth state, resolved tags, query shape,
      // result counts, and the full error). Remove after diagnosis.
      console.log('[MyDocs] step 1 — auth state:', {
        hasUser: !!user,
        uid: user?.uid,
        email: user?.email,
      });
      try {
        const viewerTags = await resolveViewerAudiences(user);
        const isAdmin = viewerTags.has('admins');
        console.log('[MyDocs] step 2 — viewer audience tags:', {
          tags: [...viewerTags],
          isAdmin,
        });

        // Also fetch the user doc directly so we can confirm the structure
        // the Firestore rule will see.
        if (user) {
          try {
            const uSnap = await getDoc(fsDoc(db, 'users', user.uid));
            console.log('[MyDocs] step 2b — user doc structure:', {
              exists: uSnap.exists(),
              roles: uSnap.exists() ? uSnap.data().roles : null,
              isParent: uSnap.exists() ? uSnap.data().isParent : null,
            });
          } catch (e) {
            console.error('[MyDocs] step 2b — user doc fetch failed:', e);
          }
        }

        let q;
        if (isAdmin) {
          q = query(
            collection(db, 'clubDocuments'),
            where('enabled', '==', true),
          );
          console.log('[MyDocs] step 3 — admin query: enabled==true (no audience filter)');
        } else {
          q = query(
            collection(db, 'clubDocuments'),
            where('enabled', '==', true),
            where('audience', 'array-contains-any', [...viewerTags]),
          );
          console.log('[MyDocs] step 3 — non-admin query:', {
            constraints: ['enabled == true', `audience array-contains-any ${JSON.stringify([...viewerTags])}`],
          });
        }
        const snap = await getDocs(q);
        if (cancelled) return;
        console.log('[MyDocs] step 4 — query succeeded. Result count:', snap.size);
        snap.docs.forEach(d => {
          console.log('[MyDocs] step 4a — doc returned:', {
            id: d.id,
            name: d.data().name,
            audience: d.data().audience,
            enabled: d.data().enabled,
          });
        });

        const visible = snap.docs
          .map(d => ({ id: d.id, ...d.data() }))
          .filter(d => {
            if (isAdmin) return true;
            const aud = Array.isArray(d.audience) ? d.audience : [];
            return aud.some(t => viewerTags.has(t));
          })
          .sort((a, b) => {
            const so = (a.sortOrder || 0) - (b.sortOrder || 0);
            if (so !== 0) return so;
            return (a.name || '').localeCompare(b.name || '');
          });
        console.log('[MyDocs] step 5 — final visible count:', visible.length);
        setDocs(visible);
      } catch (err) {
        console.error('[MyDocs] FAILED at query/filter step:', {
          message: err?.message,
          code: err?.code,
          name: err?.name,
          full: err,
        });
        logError('MyDocuments', err.message, err.stack);
        setError("Couldn't load documents. Try again in a moment.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    });
    return () => { cancelled = true; unsub(); };
  }, []);

  return (
    <Layout>
      <Box sx={{ p: 3, maxWidth: 900 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
          <ArticleIcon sx={{ color: primary, fontSize: 32 }} />
          <Typography variant="h5" sx={{ fontWeight: 700, color: primary }}>
            My Documents
          </Typography>
        </Box>
        <Typography variant="body2" sx={{ color: '#666', mb: 3 }}>
          Club documents available to you.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress sx={{ color: primary }} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : docs.length === 0 ? (
          <Paper sx={{ p: 4, textAlign: 'center' }}>
            <Typography variant="body1" sx={{ color: '#888' }}>
              No documents available right now.
            </Typography>
          </Paper>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {docs.map(d => (
              <Paper
                key={d.id}
                elevation={2}
                onClick={() => d.slug && navigate(`/docs/${d.slug}`)}
                sx={{
                  p: 2, borderRadius: 2,
                  cursor: d.slug ? 'pointer' : 'default',
                  borderLeft: `4px solid ${accent}`,
                  display: 'flex', alignItems: 'flex-start', gap: 2,
                  transition: 'all 0.15s',
                  '&:hover': d.slug ? {
                    boxShadow: 4,
                    transform: 'translateY(-1px)',
                  } : {},
                }}
              >
                <ArticleIcon sx={{ color: primary, mt: 0.5, flexShrink: 0 }} />
                <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                  <Typography variant="h6" sx={{ fontWeight: 600, color: primary }}>
                    {d.name || d.title || 'Untitled'}
                  </Typography>
                  {d.description && (
                    <Typography variant="body2" sx={{ color: '#666', mb: 1 }}>
                      {d.description}
                    </Typography>
                  )}
                  {Array.isArray(d.audience) && d.audience.length > 0 && (
                    <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap', mt: 0.5 }}>
                      {d.audience.map(t => (
                        <Chip key={t} label={AUDIENCE_LABEL[t] || t} size="small"
                          sx={{ fontSize: '0.65rem', height: 20,
                                backgroundColor: `${accent}22`,
                                color: primary, fontWeight: 600 }} />
                      ))}
                    </Box>
                  )}
                </Box>
              </Paper>
            ))}
          </Box>
        )}
      </Box>
    </Layout>
  );
}
